import { useEffect, useState } from "react";

import { faCirclePlus, faClose, faFloppyDiskPen } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Drawer } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { useGetPatientTreatmentSchedule, useGetTreatmentSchedules } from "api/services/treatment/hooks";
import type { TreatmentSchedulesItem } from "api/services/treatment/schemas/Treatment";
import { JointImage } from "routes/patients/PatientProfile/components/UserCourse/components/Templates";
import { MUISelect } from "shared/atoms/MUI";
import { sortAlphabetically } from "utils/array/sort";
import { useCurrentPatient } from "utils/hooks/useCurrentPatient";
import { translateTemplateGroup, translateTemplateKey } from "utils/translations";

import { TemplateSelectionSkeleton } from "./Skeleton";

export const DRAWER_WIDTH = 420;

interface TemplateSelectionModalProps {
  awaitingTreatmentReady?: boolean;
  patientId: number;
  onClose: () => void;
  onSelect: (templateId: number | null) => void;
}

export const TemplateSelectionModal: React.FC<TemplateSelectionModalProps> = ({
  awaitingTreatmentReady,
  patientId,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState<string>("all");
  const { currentTreatment } = useCurrentPatient();

  const {
    data: treatmentSchedulesData,
    isLoading: isTreatmentSchedulesLoading,
    isFetched: isTreatmentSchedulesFetched,
  } = useGetTreatmentSchedules({ limit: 1000 });
  const { data: patientTreatmentScheduleData, isLoading: isPatientTreatmentScheduleLoading } =
    useGetPatientTreatmentSchedule(patientId);

  const isLoading = isTreatmentSchedulesLoading || isPatientTreatmentScheduleLoading;

  const treatmentSchedules = treatmentSchedulesData?.items || [];
  const standardTemplates = treatmentSchedules?.filter(tmp => !tmp.creator_id) || [];
  const customTemplates = treatmentSchedules?.filter(tmp => tmp.creator_id) || [];

  const groupedTemplates = treatmentSchedules?.reduce((acc, template) => {
    if (template.group) {
      if (!acc[template.group]) {
        acc[template.group] = [];
      }
      acc[template.group].push(template);
    }
    return acc;
  }, {} as Record<string, TreatmentSchedulesItem[]>);

  // Template counts by group for display
  const templateCountsByGroup =
    standardTemplates?.reduce((acc, template) => {
      if (template.group) {
        acc[template.group] = (acc[template.group] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>) || {};

  // Body areas test - array of objects with label, value, and total
  const bodyAreasTest = sortAlphabetically({
    list: [
      {
        label: t("treatment.template_selection.all_templates", { count: standardTemplates?.length || 0 }),
        value: "all",
      },
      ...Array.from(
        new Set(
          standardTemplates
            ?.map(template => template.group)
            .filter((group): group is NonNullable<typeof group> => Boolean(group))
        )
      ).map(group => ({
        label: `${translateTemplateGroup(group)} (${templateCountsByGroup[group] || 0})`,
        value: String(group),
      })),
    ],
  });

  useEffect(() => {
    if (!(currentTreatment?.pain_location || currentTreatment?.ailment) || !isTreatmentSchedulesFetched) {
      return;
    }

    if (groupedTemplates?.[currentTreatment.pain_location || currentTreatment.ailment]) {
      setSelectedGroup(currentTreatment.pain_location || currentTreatment.ailment);
    }
  }, [currentTreatment?.pain_location, currentTreatment?.ailment, isTreatmentSchedulesFetched]);
  const handleStartFromScratch = () => {
    onSelect(null);
  };

  const handleContinueFromDraft = () => {
    onSelect(null);
  };

  const handleSelectTemplate = (templateId: number) => {
    onSelect(templateId);
  };

  const showSavedDraftSection =
    awaitingTreatmentReady && patientTreatmentScheduleData && patientTreatmentScheduleData.exercises.length > 0;

  return (
    <Drawer
      open
      variant="persistent"
      anchor="right"
      hideBackdrop
      sx={{
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: { md: DRAWER_WIDTH },
          boxSizing: "border-box",
        },
      }}
    >
      <ModalHeader>
        <ModalTitle>{t("treatment.template_selection.title")}</ModalTitle>
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faClose} size="lg" />
        </CloseButton>
      </ModalHeader>

      <ModalContent>
        {isLoading && <TemplateSelectionSkeleton />}

        {!isLoading && (
          <>
            {showSavedDraftSection && (
              <Section>
                <SectionTitle>{t("treatment.template_selection.continue_from_draft")}</SectionTitle>
                <SectionDescription>
                  {t("treatment.template_selection.continue_from_draft_description")}
                </SectionDescription>
                <StandardTemplatesGrid>
                  <FullWidthTemplateCard onClick={handleContinueFromDraft}>
                    <TemplateCardIcon>
                      <FontAwesomeIcon icon={faFloppyDiskPen} size="2x" />
                    </TemplateCardIcon>
                    <TemplateCardTitle>
                      {t("treatment.template_selection.continue_from_draft_button")}
                    </TemplateCardTitle>
                  </FullWidthTemplateCard>
                </StandardTemplatesGrid>
              </Section>
            )}

            <Section>
              <SectionTitle>{t("treatment.template_selection.start_from_scratch")}</SectionTitle>
              <SectionDescription>
                {t("treatment.template_selection.start_from_scratch_description")}
              </SectionDescription>
              <StandardTemplatesGrid>
                <FullWidthTemplateCard onClick={handleStartFromScratch}>
                  <TemplateCardIcon>
                    <FontAwesomeIcon icon={faCirclePlus} size="2x" />
                  </TemplateCardIcon>
                  <TemplateCardTitle>{t("treatment.template_selection.start_from_scratch_button")}</TemplateCardTitle>
                </FullWidthTemplateCard>
              </StandardTemplatesGrid>
            </Section>

            {standardTemplates.length > 0 && (
              <Section>
                <SectionTitle>{t("treatment.template_selection.joint_academy_templates")}</SectionTitle>
                <SectionDescription>
                  {t("treatment.template_selection.joint_academy_templates_description")}
                </SectionDescription>

                <FilterRow>
                  <FilterLabel>{t("treatment.template_selection.filter_by_body_area")}</FilterLabel>
                  <MUISelect
                    options={bodyAreasTest}
                    value={selectedGroup}
                    onChange={e => setSelectedGroup(e.target.value as string)}
                    sx={{ flex: 1 }}
                  />
                </FilterRow>

                <StandardTemplatesGrid>
                  {(selectedGroup === "all" ? standardTemplates : groupedTemplates?.[selectedGroup] || []).map(
                    template => (
                      <TemplateCard key={template.id} onClick={() => handleSelectTemplate(template.id)}>
                        <TemplateCardImage>{template.key && <JointImage id={template.key} />}</TemplateCardImage>
                        <TemplateCardTitle>
                          {template.key
                            ? translateTemplateKey(template.key)
                            : template.name || `Template ${template.id}`}
                        </TemplateCardTitle>
                      </TemplateCard>
                    )
                  )}
                </StandardTemplatesGrid>
              </Section>
            )}

            {customTemplates.length > 0 && (
              <Section>
                <SectionTitle>{t("treatment.template_selection.custom_templates")}</SectionTitle>
                <SectionDescription>
                  {t("treatment.template_selection.custom_templates_description")}
                </SectionDescription>
                <TemplatesGrid>
                  {customTemplates.map(template => (
                    <TemplateCard key={template.id} onClick={() => handleSelectTemplate(template.id)}>
                      <TemplateCardTitle>{template.name || `Custom Template ${template.id}`}</TemplateCardTitle>
                    </TemplateCard>
                  ))}
                </TemplatesGrid>
              </Section>
            )}
          </>
        )}
      </ModalContent>
    </Drawer>
  );
};

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border-bottom: ${({ theme }) => theme.semantic.container.border};
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1a2332;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  border-radius: 8px;
  color: #5c6f82;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background-color: #f3f5f7;
    color: #1a2332;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px;
`;

const Section = styled.div`
  margin-bottom: 48px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1a2332;
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: #5c6f82;
  margin: 0 0 20px 0;
  line-height: 1.5;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const FilterLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #5c6f82;
`;

const TemplatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const StandardTemplatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
`;

const TemplateCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 16px;
  background-color: #fafbfc;
  border: ${({ theme }) => theme.semantic.container.border};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f0f7ff;
    border-color: #0066cc;
    box-shadow: 0 4px 12px rgba(0, 102, 204, 0.1);
    transform: translateY(-2px);
  }
`;

const FullWidthTemplateCard = styled(TemplateCard)`
  grid-column: 1 / -1;
`;

const TemplateCardIcon = styled.div`
  color: #0066cc;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TemplateCardImage = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TemplateCardTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1a2332;
  text-align: center;
  line-height: 1.4;
`;
